const decodeBase64Url = (value: string): string | null => {
  try {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    return atob(padded);
  } catch {
    return null;
  }
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  const parts = token.split('.');
  if (parts.length < 2) {
    return null;
  }

  const payloadJson = decodeBase64Url(parts[1]);
  if (!payloadJson) {
    return null;
  }

  try {
    const parsed = JSON.parse(payloadJson);
    return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : null;
  } catch {
    return null;
  }
};

const toStringArray = (value: unknown): string[] => {
  if (typeof value === 'string') {
    return [value];
  }

  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }

  return [];
};

const normalizeTenantId = (tenantId: string): string =>
  tenantId.trim().toLowerCase().replace(/[^a-z0-9]/g, '');

export const extractTenantIds = (accessToken: string): string[] => {
  if (!accessToken) {
    return [];
  }

  const payload = decodeJwtPayload(accessToken);
  if (!payload) {
    return [];
  }

  const rawTenants = toStringArray(payload.tenant)
    .map(value => value.trim())
    .filter(Boolean);

  return Array.from(new Set(rawTenants));
};

export const resolveEmployeeId = (accessToken: string, tenantId: string): string | null => {
  if (!accessToken || !tenantId) {
    return null;
  }

  const payload = decodeJwtPayload(accessToken);
  if (!payload) {
    return null;
  }

  const normalizedTenantId = normalizeTenantId(tenantId);
  const rawEmployees = toStringArray(payload.employee)
    .map(value => value.trim())
    .filter(Boolean);

  for (const employeeClaim of rawEmployees) {
    const separatorIndex = employeeClaim.indexOf(':');
    if (separatorIndex <= 0 || separatorIndex >= employeeClaim.length - 1) {
      continue;
    }

    const claimTenantId = employeeClaim.slice(0, separatorIndex).trim();
    const employeeId = employeeClaim.slice(separatorIndex + 1).trim();
    if (!claimTenantId || !employeeId) {
      continue;
    }

    if (normalizeTenantId(claimTenantId) === normalizedTenantId) {
      return employeeId;
    }
  }

  return null;
};

export const resolveTenantId = (accessToken: string, preferredTenantId?: string): string | null => {
  const tokenTenants = extractTenantIds(accessToken);
  if (!tokenTenants.length) {
    return null;
  }

  const preferred = (preferredTenantId || '').trim();
  if (!preferred) {
    return tokenTenants[0];
  }

  const direct = tokenTenants.find(tenant => tenant.toLowerCase() === preferred.toLowerCase());
  if (direct) {
    return direct;
  }

  const normalizedPreferred = normalizeTenantId(preferred);
  const normalizedMatch = tokenTenants.find(tenant => normalizeTenantId(tenant) === normalizedPreferred);
  if (normalizedMatch) {
    return normalizedMatch;
  }

  return tokenTenants[0];
};

export const extractTenantPermissions = (accessToken: string, tenantId: string): string[] => {
  if (!accessToken || !tenantId) {
    return [];
  }

  const payload = decodeJwtPayload(accessToken);
  if (!payload) {
    return [];
  }

  const rawClaims = toStringArray(payload.perm);
  const expectedPrefix = `${tenantId.toLowerCase()}:`;
  const normalized = rawClaims
    .map(claim => claim.trim())
    .filter(Boolean)
    .map(claim => claim.toLowerCase())
    .filter(claim => claim.startsWith(expectedPrefix))
    .map(claim => claim.slice(expectedPrefix.length))
    .filter(Boolean);

  return Array.from(new Set(normalized)).sort();
};

export const isTokenExpired = (token: string): boolean => {
  const payload = decodeJwtPayload(token);
  if (!payload) return true;
  const exp = payload.exp;
  if (typeof exp !== 'number') return false;
  return Date.now() / 1000 > exp;
};
